import React from 'react';
import { TriggerRelation, ReminderAction } from '../../../../types/task';
import { IOSFormSelect, IOSFormNumberInput } from './IOSFormGroup';
import { useTranslation } from 'react-i18next';

// 时间单位选项
export const TIME_UNIT_OPTIONS = [
  { value: 'minutes', label: '分钟', factor: 60 },
  { value: 'hours', label: '小时', factor: 3600 },
  { value: 'days', label: '天', factor: 86400 },
];

// 触发类型选项
export const TRIGGER_TYPE_OPTIONS = [
  { value: 'RELATIVE', label: '相对时间' },
  { value: 'ABSOLUTE', label: '指定时间' },
];

// 关联点选项
export const TRIGGER_RELATION_OPTIONS = [
  { value: TriggerRelation.START, label: '开始时间' },
  { value: TriggerRelation.CALCULATED_END, label: '计算结束时间' },
  { value: TriggerRelation.DUE, label: '截止时间' },
];

import { getAllReminderMethods } from '../../../../types/notificationMapping';

// 使用统一的提醒方式配置，但为 iOS 版本调整显示标签
export const REMINDER_METHODS = getAllReminderMethods().map(method => ({
  ...method,
  label: method.value === '桌面通知' ? '推送通知' : method.label, // iOS 版本显示为"推送通知"
}));

interface IOSTimeUnitSelectorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * iOS风格的时间单位选择器
 */
export const IOSTimeUnitSelector: React.FC<IOSTimeUnitSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  className = ''
}) => {
  return (
    <IOSFormSelect
      value={value}
      onChange={onChange}
      options={TIME_UNIT_OPTIONS.map(option => ({
        value: option.value,
        label: option.label
      }))}
      disabled={disabled}
      className={className}
    />
  );
};

interface IOSDirectionSelectorProps {
  value: 'before' | 'after';
  onChange: (value: 'before' | 'after') => void;
  disabled?: boolean;
  className?: string;
}

/**
 * iOS风格的方向选择器（前/后）
 */
export const IOSDirectionSelector: React.FC<IOSDirectionSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  className = ''
}) => {
  const { t } = useTranslation(['v3/ios']);

  const directionOptions = [
    { value: 'before', label: t('v3/ios:remindersEditor.direction.before', '前') },
    { value: 'after', label: t('v3/ios:remindersEditor.direction.after', '后') },
  ];

  return (
    <IOSFormSelect
      value={value}
      onChange={onChange as (value: string) => void}
      options={directionOptions}
      disabled={disabled}
      className={className}
    />
  );
};

interface IOSTriggerTypeSelectorProps {
  value: 'RELATIVE' | 'ABSOLUTE';
  onChange: (value: 'RELATIVE' | 'ABSOLUTE') => void;
  disabled?: boolean;
  className?: string;
}

/**
 * iOS风格的触发类型选择器
 */
export const IOSTriggerTypeSelector: React.FC<IOSTriggerTypeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  className = ''
}) => {
  const { t } = useTranslation(['v3/ios']);

  const triggerTypeOptions = TRIGGER_TYPE_OPTIONS.map(option => ({
    value: option.value,
    label: t(`v3/ios:remindersEditor.triggerTypes.${option.value}`, option.label)
  }));

  return (
    <IOSFormSelect
      value={value}
      onChange={onChange as (value: string) => void}
      options={triggerTypeOptions}
      disabled={disabled}
      className={className}
    />
  );
};

interface IOSRelationSelectorProps {
  value: TriggerRelation;
  onChange: (value: TriggerRelation) => void;
  disabled?: boolean;
  className?: string;
  // 检查特定关系类型是否可用的函数
  isRelationAvailable?: (relation: TriggerRelation) => boolean;
}

/**
 * iOS风格的关联点选择器
 */
export const IOSRelationSelector: React.FC<IOSRelationSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  className = '',
  isRelationAvailable
}) => {
  const { t } = useTranslation(['v3/ios']);

  const relationOptions = TRIGGER_RELATION_OPTIONS.map(option => {
    const isAvailable = !isRelationAvailable || isRelationAvailable(option.value);
    const label = t(`v3/ios:remindersEditor.relativeToOptions.${option.value}`, option.label);

    return {
      value: option.value,
      label: isAvailable ? label : `${label} (${t('v3/ios:remindersEditor.unavailable', '不可用')})`,
      disabled: !isAvailable
    };
  });

  return (
    <IOSFormSelect
      value={value}
      onChange={onChange as (value: string) => void}
      options={relationOptions}
      disabled={disabled}
      className={className}
    />
  );
};

interface IOSReminderMethodSelectorProps {
  selectedMethods: ('短信' | '微信' | '邮件' | '桌面通知')[];
  onChange: (methods: ('短信' | '微信' | '邮件' | '桌面通知')[]) => void;
  disabled?: boolean;
  className?: string;
  showUserPreferenceHint?: boolean;
  userPreferredMethods?: ('短信' | '微信' | '邮件' | '桌面通知')[];
}

/**
 * iOS风格的提醒方式选择器
 */
export const IOSReminderMethodSelector: React.FC<IOSReminderMethodSelectorProps> = ({
  selectedMethods,
  onChange,
  disabled = false,
  className = '',
  showUserPreferenceHint = false,
  userPreferredMethods = []
}) => {
  const { t } = useTranslation(['v3/ios']);
  const handleMethodToggle = (methodValue: '短信' | '微信' | '邮件' | '桌面通知') => {
    if (selectedMethods.includes(methodValue)) {
      // 如果只有一个方法，不允许取消选择
      if (selectedMethods.length > 1) {
        onChange(selectedMethods.filter(m => m !== methodValue));
      }
    } else {
      onChange([...selectedMethods, methodValue]);
    }
  };

  return (
    <div className={className}>
      {/* 用户偏好提示 */}
      {showUserPreferenceHint && userPreferredMethods.length > 0 && (
        <div className="mb-3 p-3 bg-ios-blue/10 dark:bg-ios-dark-blue/10 rounded-ios-md">
          <div className="text-xs text-ios-blue dark:text-ios-dark-blue font-medium mb-1">
            {t('v3/ios:reminder.yourDefaultSettings', '您的默认设置')}
          </div>
          <div className="text-xs text-ios-gray dark:text-ios-dark-gray">
            {userPreferredMethods.join('、')}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        {REMINDER_METHODS.map((method) => {
          const isSelected = selectedMethods.includes(method.value);
          const isOnlySelected = selectedMethods.length === 1 && isSelected;
          const isDisabled = disabled || isOnlySelected || !method.available;
          const isUserPreferred = userPreferredMethods.includes(method.value);

          return (
            <button
              key={method.id}
              type="button"
              onClick={() => method.available && handleMethodToggle(method.value)}
              disabled={isDisabled}
              className={`
                px-3 py-2 rounded-ios-md text-sm font-medium transition-all duration-200 relative
                ${isSelected && method.available
                  ? 'bg-ios-blue dark:bg-ios-dark-blue text-white'
                  : 'bg-ios-gray6 dark:bg-ios-dark-gray5 text-ios-body dark:text-ios-dark-body'
                }
                ${isDisabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:opacity-80 active:scale-95'
                }
              `}
            >
              <div className="flex flex-col items-center">
                <div className="flex items-center">
                  <span>{method.label}</span>
                  {isUserPreferred && showUserPreferenceHint && (
                    <span className="ml-1 text-xs">⭐</span>
                  )}
                </div>
                {!method.available && (
                  <span className="text-xs text-ios-gray dark:text-ios-dark-gray mt-1">
                    {t('v3/ios:remindersEditor.comingSoon')}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

interface IOSTimeOffsetInputProps {
  value: number;
  unit: string;
  direction: 'before' | 'after';
  onValueChange: (value: number) => void;
  onUnitChange: (unit: string) => void;
  onDirectionChange: (direction: 'before' | 'after') => void;
  disabled?: boolean;
  className?: string;
}

/**
 * iOS风格的时间偏移输入组件
 */
export const IOSTimeOffsetInput: React.FC<IOSTimeOffsetInputProps> = ({
  value,
  unit,
  direction,
  onValueChange,
  onUnitChange,
  onDirectionChange,
  disabled = false,
  className = ''
}) => {
  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <div className="flex-shrink-0 w-16">
        <IOSDirectionSelector
          value={direction}
          onChange={onDirectionChange}
          disabled={disabled}
        />
      </div>
      <div className="flex-1 min-w-0">
        <IOSFormNumberInput
          value={value}
          onChange={onValueChange}
          min={1}
          disabled={disabled}
        />
      </div>
      <div className="flex-shrink-0 w-20">
        <IOSTimeUnitSelector
          value={unit}
          onChange={onUnitChange}
          disabled={disabled}
        />
      </div>
    </div>
  );
};
